/**
 * Member win-back — API client for the staff lapsed-member console.
 *
 * Follows `lib/admissions-committee/api.ts`: one BASE, one private `req()`, one
 * thin named export per endpoint. The HttpOnly cookie authenticates and
 * `fetchWithAuth` handles the silent refresh.
 */
import { fetchWithAuth, API_BASE } from "@/lib/auth";
import { saveCsvResponse, type OutcomeDef, type OutreachEntryBase, type StaffOption, type Tone } from "@/lib/campaigns/types";
import { serializeFilters, type FilterValues } from "@/lib/campaigns/urlFilters";

export type { Tone };

const BASE = `${API_BASE}/api/v1/staff/winback`;

async function req(path: string, options: RequestInit = {}) {
  const res = await fetchWithAuth(`${BASE}${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const detail = typeof data.detail === "string" ? data.detail : `Request failed (${res.status})`;
    throw Object.assign(new Error(detail), { status: res.status });
  }
  return res.json();
}

// ── Vocabulary (mirrors backend app/winback/outcomes.py) ────────────────────

export type OutcomeKey =
  | "no_answer" | "voicemail" | "will_renew" | "not_renewing" | "emailed" | "invalid_contact" | "do_not_contact";

export const OUTCOMES: OutcomeDef<OutcomeKey>[] = [
  { key: "no_answer", label: "Called – no answer", short: "No answer", tone: "amber" },
  { key: "voicemail", label: "Left voicemail", short: "Voicemail", tone: "amber" },
  { key: "will_renew", label: "Spoke – will renew", short: "Will renew", tone: "emerald" },
  { key: "not_renewing", label: "Spoke – not renewing", short: "Not renewing", tone: "red" },
  { key: "emailed", label: "Emailed", short: "Emailed", tone: "steel" },
  { key: "invalid_contact", label: "Wrong / invalid contact", short: "Invalid contact", tone: "red" },
  { key: "do_not_contact", label: "Do not contact", short: "Do not contact", tone: "slate" },
];
export const OUTCOME_BY_KEY = Object.fromEntries(OUTCOMES.map((o) => [o.key, o])) as Record<OutcomeKey, (typeof OUTCOMES)[number]>;

export type WindowKey = "0_3" | "3_9" | "3_12" | "12_plus" | "all";
export const WINDOWS: { value: WindowKey; label: string; title: string }[] = [
  { value: "0_3", label: "Last 3 mo", title: "Lapsed in the last 90 days" },
  { value: "3_9", label: "3–9 mo", title: "Lapsed 91–270 days ago" },
  { value: "3_12", label: "3–12 mo", title: "Lapsed 91–365 days ago" },
  { value: "12_plus", label: "12+ mo", title: "Lapsed over a year ago" },
  { value: "all", label: "All", title: "Every lapsed member" },
];

export type StatusKey = "lapsed" | "not_contacted" | "contacted" | "renewed";
export type SortKey = "recent" | "oldest" | "name";

export const STATUSES: { value: StatusKey; label: string }[] = [
  { value: "not_contacted", label: "Not yet contacted" },
  { value: "contacted", label: "Contacted, still lapsed" },
  { value: "renewed", label: "Renewed after outreach" },
];

export const TIERS: { value: string; label: string }[] = [
  { value: "professional", label: "Professional" },
  { value: "emeritus", label: "Emeritus" },
  { value: "affiliate", label: "Affiliate" },
  { value: "charterholder", label: "Charterholder (any tier)" },
];

export const ATTEMPTS: { value: string; label: string }[] = [
  { value: "0", label: "Never contacted" },
  { value: "1", label: "Once" },
  { value: "2plus", label: "Twice or more" },
];

// ── Filters: one spec for the URL and the API (keys are the backend's params) ──

export const WINBACK_SPEC = {
  window: "string", exp_from: "string", exp_to: "string",
  include_affiliates: "bool", charterholders_only: "bool",
  status: "list", outcome: "list", region: "list", country: "list", tier: "list", contacted_by: "list",
  attempts: "string", has_phone: "bool", contacted_from: "string", contacted_to: "string",
  q: "string", sort: "string",
} as const;

export type WinbackFilters = FilterValues<typeof WINBACK_SPEC>;

export const WINBACK_DEFAULTS: WinbackFilters = {
  window: "0_3", exp_from: null, exp_to: null, include_affiliates: false, charterholders_only: false,
  status: [], outcome: [], region: [], country: [], tier: [], contacted_by: [],
  attempts: null, has_phone: false, contacted_from: null, contacted_to: null, q: null, sort: "recent",
};

// ── Shapes ──────────────────────────────────────────────────────────────────

export interface LapsedRow {
  member_id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  country: string | null;
  region: string;
  tier: string | null;
  is_charterholder: boolean;
  exp: string | null;
  lapse_date: string;
  days_lapsed: number;
  renewed: boolean;
  latest_outcome: OutcomeKey | null;
  latest_at: string | null;
  latest_by: string | null;
  outreach_count: number;
  /** Nearest active Hub community and the geo pipeline's nearest centre city. */
  community: string | null;
  community_mi: number | null;
  centre: string | null;
}

export interface LapsedSummary {
  lapsed: number;
  not_contacted: number;
  contacted: number;
  renewed: number;
  renewal_rate: number | null;
  matching: number;
  regions: { region: string; lapsed: number; not_contacted: number; contacted: number; renewed: number; matching: number }[];
  countries: { country: string; region: string | null; count: number }[];
}

export interface LapsedPage {
  rows: LapsedRow[];
  total: number;
  summary: LapsedSummary;
  as_of: string;
}

export interface OutreachEntry extends OutreachEntryBase<OutcomeKey> {
  expiration_at_contact: string | null;
}

export interface MemberCard {
  member_id: number;
  first_name: string | null;
  last_name: string | null;
  email_1: string | null;
  email_2: string | null;
  email_3: string | null;
  phone_1: string | null;
  phone_2: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  region: string;
  community?: string | null;
  community_mi?: number | null;
  centre?: string | null;
  tier: string | null;
  membership: string | null;
  exp: string | null;
  is_charterholder: boolean;
  cmt_i_status: string | null;
  cmt_ii_status: string | null;
  cmt_iii_status: string | null;
  user_id: string | null;
  chapter: string | null;
  job_title: string | null;
  company: string | null;
  linkedin: string | null;
}

export interface MemberDetail {
  member: MemberCard;
  name: string;
  days_lapsed: number | null;
  renewed: boolean;
  outreach: OutreachEntry[];
}

export function queryString(f: WinbackFilters, extra: Record<string, string | number> = {}): string {
  const p = serializeFilters(WINBACK_SPEC, WINBACK_DEFAULTS, { ...f, q: f.q?.trim() || null });
  for (const [k, v] of Object.entries(extra)) p.set(k, String(v));
  return p.toString();
}

// ── Endpoints ───────────────────────────────────────────────────────────────

export const listLapsed = (f: WinbackFilters, limit: number, offset: number): Promise<LapsedPage> =>
  req(`/members?${queryString(f, { limit, offset })}`);

export const getFacets = (): Promise<{ contacted_by: StaffOption[]; regions: string[] }> => req("/facets");

export const getMember = (memberId: number): Promise<MemberDetail> => req(`/members/${memberId}`);

export const logOutreach = (memberId: number, outcome: OutcomeKey, note: string): Promise<OutreachEntry> =>
  req(`/members/${memberId}/outreach`, { method: "POST", body: JSON.stringify({ outcome, note }) });

export const updateOutreach = (id: string, patch: { outcome?: OutcomeKey; note?: string }): Promise<OutreachEntry> =>
  req(`/outreach/${id}`, { method: "PATCH", body: JSON.stringify(patch) });

/** Downloads the current view as CSV. The server audits every export. */
export async function downloadCsv(f: WinbackFilters): Promise<void> {
  await saveCsvResponse(await fetchWithAuth(`${BASE}/export.csv?${queryString(f)}`), "lapsed_members.csv");
}
